import React from "react";
import { Helmet } from "react-helmet";
import Header from "../Header";
import Footer from "../Footer";
import "./FunctionalTesting.css";

export default function FunctionalTesting() {
    const testAreas = [
        { title: "Gameplay Flow Testing", detail: "Validate menus, levels, scoring, timers, turns, game rules, win/loss states, replay loops, and session flow.", icon: "fa-solid fa-gamepad" },
        { title: "Multiplayer Testing", detail: "Check matchmaking, private rooms, reconnects, player status, turn sync, result screens, and real-time state changes.", icon: "fa-solid fa-users-gear" },
        { title: "Wallet & Reward Testing", detail: "Test coins, gems, wallet updates, reward claims, purchase flow, refunds, bonus logic, and transaction states.", icon: "fa-solid fa-wallet" },
        { title: "UI State Testing", detail: "Validate buttons, popups, empty states, errors, onboarding, tutorials, localization, HUDs, and responsive views.", icon: "fa-solid fa-layer-group" },
        { title: "LiveOps Testing", detail: "Check events, missions, leaderboards, offers, push triggers, daily bonuses, segments, and seasonal campaigns.", icon: "fa-solid fa-bolt" },
        { title: "Store & Release Testing", detail: "Review store links, app versions, build sanity, release notes, login flows, permissions, and post-release smoke tests.", icon: "fa-solid fa-rocket" }
    ];

    const checklist = [
        { item: "Login, signup, guest play, account recovery", type: "Account", priority: "High", status: "Core" },
        { item: "Game rules, round flow, scoring, result logic", type: "Gameplay", priority: "Critical", status: "Core" },
        { item: "Matchmaking, rooms, reconnect, player sync", type: "Multiplayer", priority: "Critical", status: "Realtime" },
        { item: "Wallet update, rewards, offer claims, refunds", type: "Economy", priority: "Critical", status: "Secure" },
        { item: "Leaderboard rank, tournament entry, prize flow", type: "Competition", priority: "High", status: "LiveOps" },
        { item: "Popups, settings, notifications, UI states", type: "Interface", priority: "Medium", status: "UX" }
    ];

    const bugFlow = [
        { step: "Find", text: "Reproduce gameplay, UI, economy, multiplayer, device, or release issue." },
        { step: "Log", text: "Document steps, expected result, actual result, screenshots, video, device, and build version." },
        { step: "Prioritize", text: "Classify severity, business impact, affected feature, and release risk." },
        { step: "Verify", text: "Retest fixes, regression areas, edge cases, and final release readiness." }
    ];

    const platforms = [
        "Android Builds",
        "iOS Builds",
        "WebGL Games",
        "Browser Games",
        "PC Builds",
        "Unity Games",
        "Unreal Games",
        "Admin Panels",
        "Player Portals",
        "Payment Flows",
        "Game Servers",
        "LiveOps Tools"
    ];

    const reports = [
        "Test Cases",
        "Bug Reports",
        "Severity Matrix",
        "Regression Notes",
        "Build Smoke Report",
        "Release Checklist",
        "Device Notes",
        "QA Summary"
    ];

    const process = [
        { step: "01", title: "Feature Review", text: "Understand game rules, user journeys, admin workflows, backend events, and release requirements." },
        { step: "02", title: "Test Case Planning", text: "Prepare test scenarios for gameplay, UI, multiplayer, wallet, rewards, LiveOps, and edge cases." },
        { step: "03", title: "Functional QA", text: "Execute tests across builds, devices, accounts, roles, player states, and game modes." },
        { step: "04", title: "Bug Reporting", text: "Log clear bugs with reproduction steps, videos, screenshots, severity, device details, and build version." },
        { step: "05", title: "Regression & Signoff", text: "Verify fixes, retest impacted flows, run smoke checks, and provide release readiness notes." }
    ];

    const faqs = [
        {
            question: "What is functional testing for games?",
            answer: "Functional game testing checks whether gameplay rules, UI flows, account actions, wallet logic, multiplayer states, rewards, events, and release flows work as expected."
        },
        {
            question: "Can you test multiplayer games?",
            answer: "Yes. We test matchmaking, room creation, private tables, player sync, reconnect states, turn flow, result screens, and server-driven gameplay behavior."
        },
        {
            question: "Do you test wallet and reward flows?",
            answer: "Yes. We test coins, cash, gems, offers, rewards, refunds, purchases, bonus claims, leaderboards, tournament prizes, and transaction states."
        },
        {
            question: "What QA reports do you provide?",
            answer: "We can provide test cases, bug reports, severity notes, screenshots, videos, device details, regression reports, smoke test notes, and release signoff summaries."
        }
    ];

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Game Functional Testing Services",
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": "Functional Testing",
        "areaServed": "Worldwide",
        "description": "Game functional testing services for gameplay flows, multiplayer features, wallet and rewards, UI states, LiveOps events, admin panels, releases, bug reporting, regression testing, and QA signoff."
    };

    return (
        <>
            <Helmet>
                <title>Game Functional Testing Services | QA Testing for Games | Oengines</title>
                <meta name="description" content="Oengines Games LLP provides game functional testing for gameplay flows, multiplayer, wallets, rewards, UI states, LiveOps events, releases, bug reports, regression, and QA signoff." />
                <meta name="keywords" content="game functional testing, game QA testing, mobile game testing, multiplayer game testing, gameplay testing, wallet testing, LiveOps testing, game regression testing" />
                <link rel="canonical" href="https://www.oengines.com/game-functional-testing" />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className="functionalqa-page">
                <section className="functionalqa-hero">
                    <div className="container">
                        <div className="functionalqa-hero-grid">
                            <div>
                                <span className="functionalqa-eyebrow">Game Functional Testing</span>
                                <h1>Find Gameplay, UI, Wallet & Multiplayer Issues Before Players Do</h1>
                                <p>Oengines Games LLP provides functional QA testing for mobile games, web games, multiplayer platforms, real-money games, fantasy sports apps, admin panels, LiveOps tools, and release builds.</p>
                                <div className="functionalqa-actions">
                                    <a href="/contact" className="as-btn">Plan QA Testing <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="functionalqa-call">Call: (0261) - 4054978</a>
                                </div>
                            </div>
                            <div className="functionalqa-lab">
                                <div className="functionalqa-lab-top">
                                    <span></span><span></span><span></span>
                                    <strong>QA Test Lab</strong>
                                </div>
                                <div className="functionalqa-lab-body">
                                    <div className="functionalqa-score">
                                        <strong>96%</strong>
                                        <span>Release Flow Coverage</span>
                                    </div>
                                    <div className="functionalqa-bars">
                                        <span></span><span></span><span></span><span></span>
                                    </div>
                                    <div className="functionalqa-lab-list">
                                        <span>Gameplay Rules</span>
                                        <span>Wallet Logic</span>
                                        <span>Multiplayer Sync</span>
                                        <span>Regression Pass</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="functionalqa-area-section space">
                    <div className="container">
                        <div className="functionalqa-heading text-center">
                            <span>Testing Coverage</span>
                            <h2>Functional QA Areas for Game Products</h2>
                            <p>We test the workflows that directly affect player trust, retention, monetization, gameplay fairness, and release quality.</p>
                        </div>
                        <div className="functionalqa-area-grid">
                            {testAreas.map((item) => (
                                <article key={item.title}>
                                    <i className={item.icon}></i>
                                    <h3>{item.title}</h3>
                                    <p>{item.detail}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="functionalqa-checklist-section space">
                    <div className="container">
                        <div className="functionalqa-split-heading">
                            <div className="functionalqa-heading mb-0">
                                <span>QA Checklist</span>
                                <h2>Game Functional Test Matrix</h2>
                            </div>
                            <p>Our QA structure covers core gameplay, account flows, real-time states, economy logic, competition systems, and player-facing UI behavior.</p>
                        </div>
                        <div className="functionalqa-table-wrap">
                            <table className="functionalqa-table">
                                <thead>
                                    <tr>
                                        <th>Test Scenario</th>
                                        <th>Area</th>
                                        <th>Priority</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {checklist.map((item) => (
                                        <tr key={item.item}>
                                            <td>{item.item}</td>
                                            <td>{item.type}</td>
                                            <td>{item.priority}</td>
                                            <td><span>{item.status}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>

                <section className="functionalqa-bug-section space">
                    <div className="container">
                        <div className="functionalqa-heading text-center">
                            <span>Bug Workflow</span>
                            <h2>Clear Bug Reporting for Faster Fixes</h2>
                        </div>
                        <div className="functionalqa-bug-flow">
                            {bugFlow.map((item) => (
                                <article key={item.step}>
                                    <strong>{item.step}</strong>
                                    <p>{item.text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="functionalqa-platform-section space">
                    <div className="container">
                        <div className="functionalqa-platform-panel">
                            <div className="functionalqa-heading mb-0">
                                <span>Platforms & Deliverables</span>
                                <h2>Testing Across Builds, Devices, Game Modes & Releases</h2>
                                <p>Functional testing can cover player-facing builds, web games, backend-driven features, admin panels, wallet flows, and release readiness.</p>
                            </div>
                            <div>
                                <div className="functionalqa-chip-list">
                                    {platforms.map((item) => <span key={item}>{item}</span>)}
                                </div>
                                <div className="functionalqa-report-list">
                                    {reports.map((item) => <strong key={item}>{item}</strong>)}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="functionalqa-visual-section space">
                    <div className="container">
                        <div className="row align-items-center g-5">
                            <div className="col-lg-5">
                                <div className="functionalqa-heading">
                                    <span>Game QA Review</span>
                                    <h2>Test Real Player Paths, Not Only Screens</h2>
                                    <p>Game QA needs repeated playthroughs, account state changes, wallet checks, multiplayer sessions, event timing, and regression around previously fixed bugs.</p>
                                </div>
                            </div>
                            <div className="col-lg-7">
                                <div className="functionalqa-image-grid">
                                    <img src="assets/img/project/Faster_fixes.jpg" alt="Game QA bug fixing" />
                                    <img src="assets/img/GamesImg/dashboard-07.png" alt="QA dashboard" />
                                    <img src="assets/img/GameDevelopment/SCREEN4.png" alt="Gameplay testing screen" />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="functionalqa-process-section space">
                    <div className="container">
                        <div className="functionalqa-heading text-center">
                            <span>Testing Process</span>
                            <h2>From Feature Review to Release Signoff</h2>
                        </div>
                        <div className="functionalqa-process-grid">
                            {process.map((item) => (
                                <article key={item.step}>
                                    <span>{item.step}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="functionalqa-faq-section space">
                    <div className="container">
                        <div className="functionalqa-heading text-center">
                            <span>FAQ</span>
                            <h2>Game Functional Testing Questions</h2>
                        </div>
                        <div className="functionalqa-faq-grid">
                            {faqs.map((item) => (
                                <article key={item.question}>
                                    <h3>{item.question}</h3>
                                    <p>{item.answer}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="functionalqa-cta">
                    <div className="container">
                        <div className="functionalqa-cta-box">
                            <div>
                                <span>QA Testing Consultation</span>
                                <h2>Need functional testing for your game build?</h2>
                                <p>Share your game type, platform, build, known issues, release timeline, multiplayer needs, wallet flows, and test priorities.</p>
                            </div>
                            <a href="/contact" className="as-btn">Start Functional QA <i className="fa-regular fa-arrow-right ms-2"></i></a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
